/**
 * One delivery's card (order to invoice doc 104 §6, §9.1), as the checkout's delivery preview answers it. A card says
 * what it carries and where it goes, and then one of:
 *   - Included: the supplier's price includes delivery; nothing to choose.
 *   - Ship: every courier option at its price and an agreed price with its reason, as the
 *     item page's "Courier service" list; its price, or "Not yet priced" in the server's words.
 *   - Collect: from one of our collection points, and who is collecting.
 * Every price, level, date and sentence is the preview's; the card only holds what staff picked and reports it back
 * to be read again. A delivery whose price moved under a refused checkout says so. *Change delivery…* on an order
 * draws it.
 */
import { useId, useRef } from "react";
import type { DeliveryMode } from "@shared-core/data/delivery-types";
import type {
  BasketCollectionPoint,
  BasketDelivery,
} from "@shared-core/data/basket-types";
import { deliveryFromWire } from "@shared-core/data/catalogue-delivery-wire";
import type { CatalogueDelivery } from "@shared-core/data/catalogue-types";
import type {
  ChangedDeliveryPrice,
  DeliveryField,
} from "@shared-core/domain/basket-presentation";
import {
  NO_PICK,
  NOT_YET_PRICED,
  collectionPointOf,
  deliveryDestinationText,
  deliveryLinesText,
  formatPriceOrNotYetPriced,
  includedDeliveryReading,
  isChoosableServiceLevel,
  type DeliveryPick,
} from "@shared-core/domain/checkout-delivery";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import { Card } from "../../primitives/card";
import { Checkbox } from "../../primitives/checkbox";
import { Label } from "../../primitives/label";
import { CollectionFields } from "./CollectionFields";
import { CourierServiceList, type CourierChoice } from "./CourierServiceList";
import { DeliveryModePills } from "./DeliveryModePills";

/** The server's refusals of a delivery's inputs, by delivery, shown beside the input they name. */
export type BasketDeliveryErrors = Record<
  string,
  Partial<Record<DeliveryField, string>>
>;

/** What a pick changes, and whether the preview must be read again for it (a typed word does not move a price). */
export type OnBasketDeliveryPick = (
  deliveryRef: string,
  patch: Partial<DeliveryPick>,
  options?: { preview?: boolean },
) => void;

const MODES: readonly DeliveryMode[] = ["ship", "collect"];

/** 980 → "9.80": an agreed price as the input holds it. */
const poundsOf = (pence: number) => (pence / 100).toFixed(2);

/** One delivery's card: what it carries, where it goes, and its choice. *Change delivery…* (doc 106 §9.4) shows
 * the delivery as it was ("Was …") and, for an agreed price, "Keep the agreed price". */
export function DeliveryCard({
  delivery,
  points,
  pick: picked,
  errors,
  change,
  disabled,
  onPick,
  was = null,
  keepAgreedPrice = null,
}: {
  delivery: BasketDelivery;
  points: BasketCollectionPoint[];
  /** Undefined until staff change this delivery: it stands as the preview has it. */
  pick: DeliveryPick | undefined;
  errors: Partial<Record<DeliveryField, string>>;
  change: ChangedDeliveryPrice | null;
  disabled: boolean;
  onPick: OnBasketDeliveryPick;
  /** "Was Acme Exeter · Next day · £9.80": the delivery before a change, above its new reading. */
  was?: string | null;
  /** An agreed price a change keeps unless unticked (then the delivery is priced again at its level). */
  keepAgreedPrice?: {
    isKept: boolean;
    onChange: (isKept: boolean) => void;
  } | null;
}) {
  const id = useId();
  const headingId = `${id}-heading`;
  const ref = delivery.delivery_ref;
  const priced = delivery.delivery;
  const pick = picked ?? NO_PICK;
  // A collected delivery comes back without its levels: remember them so staff can go back to shipping it.
  const optionsSeen = useRef(priced.options ?? []);
  if (priced.options && priced.options.length > 0)
    optionsSeen.current = priced.options;
  const lines = deliveryLinesText(delivery.lines);
  const destination = deliveryDestinationText(delivery);
  const isCollected = picked
    ? picked.isCollected
    : priced.status === "collection";
  const canCollect = points.length > 0;
  const recipient = [delivery.recipient_name, delivery.recipient_phone]
    .filter(Boolean)
    .join(", ");

  return (
    <li>
      <Card className="gap-3 p-4">
        <div className="space-y-0.5">
          <h3 id={headingId} className="text-sm font-medium">
            {isCollected || !destination ? lines : `${lines} to ${destination}`}
          </h3>
          {!isCollected && recipient && (
            <p className="text-sm text-muted-foreground">
              Recipient: {recipient}
            </p>
          )}
          {was && <p className="text-sm text-muted-foreground">{was}</p>}
        </div>
        {keepAgreedPrice && (
          <div className="flex items-center gap-2">
            <Checkbox
              id={`${id}-keep-agreed`}
              checked={keepAgreedPrice.isKept}
              onCheckedChange={(checked) =>
                keepAgreedPrice.onChange(checked === true)
              }
              disabled={disabled}
            />
            <Label htmlFor={`${id}-keep-agreed`} className="font-normal">
              Keep the agreed price
            </Label>
          </div>
        )}

        {priced.status === "included" ? (
          <p className="text-sm">
            {includedDeliveryReading(
              delivery.service_level_label,
              priced.courier_name ?? null,
            )}
          </p>
        ) : (
          <>
            {canCollect && (
              <DeliveryModePills
                current={isCollected ? "collect" : "ship"}
                modes={MODES}
                ariaLabel={`How ${lines} goes`}
                onChange={(mode) =>
                  onPick(ref, { isCollected: mode === "collect" })
                }
                disabled={disabled}
              />
            )}
            {isCollected ? (
              <Collection
                delivery={delivery}
                points={points}
                pick={pick}
                errors={errors}
                disabled={disabled}
                onPick={onPick}
              />
            ) : (
              <Ship
                delivery={delivery}
                options={optionsSeen.current}
                pick={pick}
                errors={errors}
                disabled={disabled}
                onPick={onPick}
              />
            )}
            <PriceRow
              delivery={delivery}
              isCollected={isCollected}
              change={change}
            />
          </>
        )}
      </Card>
    </li>
  );
}

/** The levels at their price and the agreed price, as the item page's "Courier service" list. */
function Ship({
  delivery,
  options,
  pick,
  errors,
  disabled,
  onPick,
}: {
  delivery: BasketDelivery;
  /** The delivery's courier options (the last it came with: a collection has none). */
  options: BasketDelivery["delivery"]["options"];
  pick: DeliveryPick;
  errors: Partial<Record<DeliveryField, string>>;
  disabled: boolean;
  onPick: OnBasketDeliveryPick;
}) {
  const ref = delivery.delivery_ref;
  const priced: CatalogueDelivery = deliveryFromWire({
    ...delivery.delivery,
    options,
  });
  if (priced.status === "address_required")
    return (
      <p className="text-sm text-muted-foreground">
        Choose the delivery address.
      </p>
    );
  const isAgreed = pick.agreed !== null;
  // Untouched, a delivery agreed on the item page stands as the agreed row, with its price and reason.
  const standsAgreed =
    !isAgreed && pick.serviceLevel === null && priced.source === "agreed";
  const agreed =
    pick.agreed ??
    (standsAgreed && priced.deliveryPricePence !== null
      ? {
          price: poundsOf(priced.deliveryPricePence),
          reason: delivery.agreed_delivery_reason ?? "",
        }
      : { price: "", reason: "" });
  // The courier and level staff picked, else those the lines chose on the item page and the preview priced.
  const level =
    pick.serviceLevel ??
    (priced.source === "courier_quote" ? priced.serviceLevel : null);
  const cheapestAtLevel = priced.options
    .filter(
      (o) =>
        o.serviceLevel === level &&
        o.isAvailable &&
        o.deliveryPricePence != null,
    )
    .sort((x, y) => x.deliveryPricePence! - y.deliveryPricePence!)[0];
  const courierSupplierId = pick.serviceLevel
    ? (pick.courierSupplierId ?? cheapestAtLevel?.courierSupplierId ?? null)
    : priced.courierSupplierId;
  const choice: CourierChoice =
    isAgreed || standsAgreed
      ? { kind: "agreed" }
      : level
        ? { kind: "option", serviceLevel: level, courierSupplierId }
        : { kind: "default" };
  return (
    <CourierServiceList
      delivery={priced}
      choice={choice}
      onChoose={(next) => {
        if (next.kind === "agreed") {
          onPick(ref, { serviceLevel: null, courierSupplierId: null, agreed });
        } else if (next.kind === "option") {
          onPick(ref, {
            serviceLevel: isChoosableServiceLevel(next.serviceLevel)
              ? next.serviceLevel
              : null,
            courierSupplierId: next.courierSupplierId,
            agreed: null,
          });
        } else {
          // "Clear agreed price": a delivery agreed on the item page stands agreed while nothing is picked, so
          // clearing it picks the default level; with no courier to pick, the agreed price is all there is.
          const fallback = priced.options.find(
            (o) => o.isDefault && o.isAvailable,
          );
          onPick(ref, {
            serviceLevel:
              fallback && isChoosableServiceLevel(fallback.serviceLevel)
                ? fallback.serviceLevel
                : null,
            courierSupplierId: fallback?.courierSupplierId ?? null,
            agreed: null,
          });
        }
      }}
      agreedPrice={agreed.price}
      onAgreedPriceChange={(price) =>
        onPick(ref, { agreed: { ...agreed, price } }, { preview: false })
      }
      agreedReason={agreed.reason}
      onAgreedReasonChange={(reason) =>
        onPick(ref, { agreed: { ...agreed, reason } }, { preview: false })
      }
      onAgreedBlur={() => onPick(ref, { agreed })}
      canClearAgreed={
        priced.source !== "agreed" ||
        priced.options.some((o) => o.isDefault && o.isAvailable)
      }
      choiceError={errors.service_level ?? null}
      agreedPriceError={errors.agreed_delivery_price_pence ?? null}
      agreedReasonError={errors.agreed_delivery_reason ?? null}
      disabled={disabled}
    />
  );
}

/** Where it is collected from, and who is collecting. */
function Collection({
  delivery,
  points,
  pick,
  errors,
  disabled,
  onPick,
}: {
  delivery: BasketDelivery;
  points: BasketCollectionPoint[];
  pick: DeliveryPick;
  errors: Partial<Record<DeliveryField, string>>;
  disabled: boolean;
  onPick: OnBasketDeliveryPick;
}) {
  const ref = delivery.delivery_ref;
  // Until staff type, who collects stands as the preview has it (the order's contact).
  const collector = pick.collector ?? {
    name:
      delivery.delivery.status === "collection"
        ? (delivery.recipient_name ?? "")
        : "",
    phone:
      delivery.delivery.status === "collection"
        ? (delivery.recipient_phone ?? "")
        : "",
  };
  return (
    <CollectionFields
      state={{ status: "ready", points }}
      pointId={collectionPointOf(pick, points)}
      onPointChange={(collectionPointId) => onPick(ref, { collectionPointId })}
      pointError={errors.collection_point_address_id ?? errors.collect ?? null}
      collectorName={collector.name}
      onCollectorNameChange={(name) =>
        onPick(ref, { collector: { ...collector, name } }, { preview: false })
      }
      collectorNameError={errors.recipient_name ?? null}
      collectorPhone={collector.phone}
      onCollectorPhoneChange={(phone) =>
        onPick(ref, { collector: { ...collector, phone } }, { preview: false })
      }
      collectorPhoneError={errors.recipient_phone ?? null}
      onCollectorBlur={() => onPick(ref, { collector })}
      disabled={disabled}
    />
  );
}

/** "Delivery  £9.80", "Collection", or "Not yet priced" with the server's reason; never £0 for a delivery with none. */
function PriceRow({
  delivery,
  isCollected,
  change,
}: {
  delivery: BasketDelivery;
  isCollected: boolean;
  change: ChangedDeliveryPrice | null;
}) {
  const priced = delivery.delivery;
  // Until the preview confirms a collection (it waits for a point to be chosen), there is no price to say.
  if (priced.status === "address_required") return null;
  if (isCollected !== (priced.status === "collection")) return null;
  const value =
    priced.status === "collection"
      ? "Collection"
      : priced.status === "not_yet_priced"
        ? NOT_YET_PRICED
        : formatPriceOrNotYetPriced(priced.delivery_price_pence ?? null);
  return (
    <div className="space-y-1 border-t border-border pt-3">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span>Delivery</span>
        <span className="font-medium tabular-nums">{value}</span>
      </div>
      {change && change.oldPricePence !== null && (
        <p className="text-sm text-red-700 dark:text-red-400">
          Was {formatQuotedPrice(change.oldPricePence)}
        </p>
      )}
      {priced.status === "not_yet_priced" && priced.not_priced_message && (
        <p className="text-sm text-muted-foreground">
          {priced.not_priced_message}
        </p>
      )}
    </div>
  );
}
